<template lang="pug">
  CrmFunnelFormUi(
    v-if="ready"
    :stages="stages"
    :rejectionReasons="rejectionReasons"
    :serviceTypes="serviceTypes"
    :funnelName="funnelName"
    :withDefaults="withDefaults"
    :createMode="createMode"
    :showSaveName="showSaveName"
    :stagesSaving="stagesSaving"
    :reasonsSaving="reasonsSaving"
    :serviceTypesSaving="serviceTypesSaving"
    @updateName="funnelName = $event"
    @updateWithDefaults="withDefaults = $event"
    @saveName="saveName"
    @deleteFunnel="deleteFunnel"
    @addStage="addItem('stages', $event)"
    @renameStage="renameItem('stages', $event)"
    @deleteStage="deleteItem('stages', $event)"
    @reorderStages="reorderItems('stages', $event)"
    @addReason="addItem('reasons', $event)"
    @renameReason="renameItem('reasons', $event)"
    @deleteReason="deleteItem('reasons', $event)"
    @reorderReasons="reorderItems('reasons', $event)"
    @addServiceType="addItem('serviceTypes', $event)"
    @renameServiceType="renameItem('serviceTypes', $event)"
    @deleteServiceType="deleteItem('serviceTypes', $event)"
    @reorderServiceTypes="reorderItems('serviceTypes', $event)"
    )
</template>

<script>
import crmModel from '@/model/crm';
import popupModel from '@/model/popup';

import crmFunnelApi from '@/api/crmFunnel';
import crmStageApi from '@/api/crmStage';
import crmRejectionReasonApi from '@/api/crmRejectionReason';
import crmServiceTypeApi from '@/api/crmServiceType';

import CrmFunnelFormUi from '@/components/crm/CrmFunnelFormUi.vue';

/**
 * Три списка воронки устроены одинаково: свой эндпоинт, своё поле в data, свой
 * порядок на самой воронке и свои подписи в сообщениях. Держим это таблицей —
 * иначе на каждый список пришлось бы по четыре почти одинаковых метода, то
 * есть двенадцать на всю форму.
 *
 * hasColor есть только у этапов: остальным сущностям сервер цвет не принимает.
 */
const LISTS = {
  stages: {
    api: crmStageApi,
    prop: 'stages',
    saving: 'stagesSaving',
    orderParam: 'stagesOrder',
    hasColor: true,
    confirmText: 'Удалить этап?',
    errors: {
      add: 'Не удалось добавить этап',
      save: 'Не удалось сохранить этап',
      remove: 'Не удалось удалить этап',
      blocked: 'На этапе есть заявки. Перенесите их на другой этап и повторите',
      order: 'Не удалось сохранить порядок этапов',
    },
  },
  reasons: {
    api: crmRejectionReasonApi,
    prop: 'rejectionReasons',
    saving: 'reasonsSaving',
    orderParam: 'reasonsOrder',
    hasColor: false,
    confirmText: 'Удалить причину отказа?',
    errors: {
      add: 'Не удалось добавить причину отказа',
      save: 'Не удалось сохранить причину отказа',
      remove: 'Не удалось удалить причину отказа',
      blocked: 'Причина указана в заявках. Смените её в этих заявках и повторите',
      order: 'Не удалось сохранить порядок причин отказа',
    },
  },
  serviceTypes: {
    api: crmServiceTypeApi,
    prop: 'serviceTypes',
    saving: 'serviceTypesSaving',
    orderParam: 'serviceTypesOrder',
    hasColor: false,
    confirmText: 'Удалить тип услуги?',
    errors: {
      add: 'Не удалось добавить тип услуги',
      save: 'Не удалось сохранить тип услуги',
      remove: 'Не удалось удалить тип услуги',
      blocked: 'Тип услуги указан в заявках. Смените его в этих заявках и повторите',
      order: 'Не удалось сохранить порядок типов услуг',
    },
  },
};

export default {
  name: 'CrmFunnelFormApi',
  components: { CrmFunnelFormUi },
  props: {
    // воронка всегда создаётся с привязкой к текущей компании
    companyId: {
      type: [Number, String],
      required: true,
    },
    // null — форма открыта на создание
    funnelId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      // собственная копия funnelId: после создания форма переключается
      // в режим редактирования, не переоткрываясь
      id: this.funnelId,
      funnelName: '',
      // последнее сохранённое на сервере название — расхождение с funnelName
      // и есть условие показа кнопки «Сохранить»
      savedName: '',
      // наполнить новую воронку наборами по умолчанию; спрашиваем только при
      // создании, поэтому после первого сохранения значение уже ни на что
      // не влияет
      withDefaults: true,
      stages: [],
      rejectionReasons: [],
      serviceTypes: [],
      ready: false,
      // индикатор операций над списком (сортировка, удаление): null — пока
      // показывать нечего. Добавление и переименование сюда не входят —
      // там состояние показывает индикатор в самой строке
      stagesSaving: null,
      reasonsSaving: null,
      serviceTypesSaving: null,
    };
  },
  created() {
    // Очередь сохранений порядка, по одной на список (см. saveOrder).
    // Не состояние экрана, реактивными быть не должны
    this.orderSaves = {};
    this.orderSaveAgain = {};
    Object.keys(LISTS).forEach((key) => {
      this.orderSaves[key] = null;
      this.orderSaveAgain[key] = false;
    });

    if (this.id) this.load();
    else this.ready = true;
  },
  computed: {
    createMode() { return !this.id; },
    funnelIri() { return crmFunnelApi.getIri(this.id); },
    showSaveName() { return this.createMode || this.funnelName !== this.savedName; },
  },
  methods: {
    async load() {
      const funnel = await crmModel.run(
        () => crmModel.fetchFunnel(this.id),
        'Не удалось загрузить воронку',
      );
      if (!funnel) return;

      this.funnelName = funnel.name;
      this.savedName = funnel.name;
      // этапы, причины отказа и типы услуг приходят вложенными в саму воронку
      this.stages = crmModel.funnelStages(funnel);
      this.rejectionReasons = crmModel.funnelReasons(funnel);
      this.serviceTypes = crmModel.funnelServiceTypes(funnel);
      this.ready = true;
    },

    saveName() {
      return this.createMode ? this.createFunnel() : this.renameFunnel();
    },

    async createFunnel() {
      const name = this.funnelName.trim();
      if (!name) return;

      const funnel = await crmModel.run(
        () => crmFunnelApi.create({
          name,
          company: `/api/companies/${this.companyId}`,
          withDefaults: this.withDefaults,
        }, true),
        'Не удалось создать воронку',
      );
      if (!funnel) return;

      this.id = funnel.id;
      this.$emit('change', funnel.id);
      // Перечитываем воронку целиком, а не берём ответ на создание: при
      // withDefaults сервер сам завёл этапы, причины отказа и типы услуг, и
      // без этого запроса вкладки открылись бы пустыми
      await this.load();
    },

    async renameFunnel() {
      const name = this.funnelName.trim();
      if (!name) return;

      const funnel = await crmModel.run(
        () => crmFunnelApi.update(this.id, { name }, true),
        'Не удалось сохранить название воронки',
      );
      if (!funnel) return;

      this.funnelName = funnel.name;
      this.savedName = funnel.name;
      this.$emit('change', this.id);
    },

    async deleteFunnel() {
      const confirmed = await popupModel.addConfirm(
        'Удалить воронку?',
        `Вместе с воронкой «${this.savedName}» будут удалены её этапы, причины отказа, типы услуг и участники`,
      );
      if (!confirmed) return;

      const result = await crmModel.run(
        () => crmModel.deleteWithLeadsCheck(
          () => crmFunnelApi.delete(this.id, true),
          'В воронке есть заявки. Удалите их или перенесите в другую воронку, затем повторите',
        ),
        'Не удалось удалить воронку',
      );
      if (result === null) return;

      this.$emit('change', null);
      this.$emit('close');
    },

    // Показывает индикатор списка на время операции. При ошибке индикатор
    // убирается совсем (null), а не показывает «Сохранено» — про неудачу
    // пользователю уже сообщил алерт из crmModel.run
    async withListIndicator(key, action) {
      const { saving } = LISTS[key];

      this[saving] = true;
      const ok = await action();
      this[saving] = ok ? false : null;
      return ok;
    },

    /**
     * Порядок списков хранится на самой воронке (stagesOrder / reasonsOrder /
     * serviceTypesOrder), поэтому переписывать его нужно не только при
     * drag&drop, но и после добавления или удаления элемента.
     *
     * Отправляем строго по одному запросу на список. Два PATCH, ушедшие
     * одновременно — быстро добавили два этапа подряд, удалили один во время
     * сортировки, — могут прийти на сервер в обратном порядке, и в базе
     * останется устаревший состав.
     *
     * Просьбы, поступившие пока запрос летит, не копятся, а сводятся к одной:
     * состав собирается в момент отправки (см. sendOrder), поэтому один
     * заключительный PATCH уже несёт актуальный порядок, а промежуточные были
     * бы одинаковыми и лишними.
     */
    saveOrder(key) {
      if (this.orderSaves[key]) {
        this.orderSaveAgain[key] = true;
        return this.orderSaves[key];
      }

      const run = async () => {
        let result = await this.sendOrder(key);

        while (this.orderSaveAgain[key]) {
          this.orderSaveAgain[key] = false;
          result = await this.sendOrder(key);
        }

        this.orderSaves[key] = null;
        return result;
      };

      this.orderSaves[key] = run();
      return this.orderSaves[key];
    },

    sendOrder(key) {
      const { prop, orderParam, errors } = LISTS[key];
      const params = { [orderParam]: this[prop].map(item => item.id) };

      return crmModel.run(() => crmFunnelApi.update(this.id, params), errors.order);
    },

    // done приходит из CrmFunnelFormUi: строка редактирования снимается
    // только после ответа сервера, иначе элемент моргает
    async addItem(key, { name, color, done }) {
      const { api, prop, hasColor, errors } = LISTS[key]; // eslint-disable-line object-curly-newline
      const params = { name, funnel: this.funnelIri };
      if (hasColor) params.color = color;

      const item = await crmModel.run(() => api.create(params), errors.add);
      if (!item) {
        done?.(false);
        return;
      }

      this[prop].push(item);
      done?.(true);
      await this.saveOrder(key);
    },

    async renameItem(key, { id, name, color, done }) { // eslint-disable-line object-curly-newline
      const { api, prop, hasColor, errors } = LISTS[key]; // eslint-disable-line object-curly-newline
      const params = hasColor ? { name, color } : { name };

      const item = await crmModel.run(() => api.update(id, params), errors.save);
      if (!item) {
        done?.(false);
        return;
      }

      const index = this[prop].findIndex(i => i.id === id);
      if (index !== -1) this.$set(this[prop], index, item);
      done?.(true);
    },

    async deleteItem(key, id) {
      const { api, prop, confirmText, errors } = LISTS[key]; // eslint-disable-line object-curly-newline

      const item = this[prop].find(i => i.id === id);
      const confirmed = await popupModel.addConfirm(confirmText, item?.name ?? '');
      if (!confirmed) return;

      await this.withListIndicator(key, async () => {
        const result = await crmModel.run(
          () => crmModel.deleteWithLeadsCheck(() => api.delete(id), errors.blocked),
          errors.remove,
        );
        if (result === null) return false;

        this[prop] = this[prop].filter(i => i.id !== id);
        return this.saveOrder(key);
      });
    },

    // orderedIds — полный список id в новом порядке, как его перетащили
    // в CrmFunnelFormUi
    async reorderItems(key, orderedIds) {
      const { prop } = LISTS[key];

      this[prop].sort((a, b) => orderedIds.indexOf(a.id) - orderedIds.indexOf(b.id));
      await this.withListIndicator(key, () => this.saveOrder(key));
    },
  },
};
</script>
